import { cn } from '@/lib/utils';
import { useChatStore } from '@/store/chatStore';
import { useConfigStore } from '@/store/configStore';
import { translateConfigValue } from '@/utils/translateConfigValue';
import { Maximize2, Minimize2, Settings, X } from 'lucide-react';
import useBreakpoints from './hooks/useBreakpoints';

const RestartSvg = ({ className }: { className?: string }) => {
  return (
    <svg
      className={className}
      width="19"
      height="17"
      viewBox="0 0 19 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.5 6.79329C17.5 6.79329 15.8292 4.51681 14.4718 3.1585C13.1144 1.80018 11.2387 0.959961 9.16669 0.959961C5.02455 0.959961 1.66669 4.31783 1.66669 8.45996C1.66669 12.6021 5.02455 15.96 9.16669 15.96C12.5859 15.96 15.4708 13.6719 16.3735 10.5433M17.5 6.79329V1.79329M17.5 6.79329H12.5"
        stroke="currentColor"
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const ChatHeader = () => {
  const { fullScreen, toggleFullScreen, toggleOpen, resetSession } = useChatStore();
  const { isMobile } = useBreakpoints();

  const { config } = useConfigStore();
  const headerConfig = config.ui?.header || {};
  const {
    haveIcon,
    icon,
    haveTitle,
    haveSupportiveText,
    haveFullScreenMode,
    haveRestartButton,
    haveSettingsButton,
  } = headerConfig;

  const language = config.language ?? 'de';
  const title = translateConfigValue(headerConfig.title, language);
  const supportiveText = translateConfigValue(headerConfig.supportiveText, language);

  const generalSpecificVariant = config.ui?.generalSpecificVariant;

  const buttonClassName =
    generalSpecificVariant === 'velio'
      ? cn('rounded-md border-r-[1px] border-r-[#8C8C8C] !rounded-[0] pr-[8px] hover:opacity-80')
      : cn('rounded-full p-2 hover:bg-gray-100');

  const iconClassName =
    generalSpecificVariant === 'velio'
      ? cn({
          'text-[#0D0D0D]': fullScreen,
          'text-widget-nav-icon': !fullScreen,
        })
      : cn('text-widget-nav-icon');

  return (
    <div
      className={cn(
        'mx-[-16px] flex w-full max-w-[780px] items-center justify-between border-b border-gray-200',
        {
          'px-[16px] py-4 md:mx-[-20px] md:px-[20px] lg:mx-[-32px] lg:px-[32px]':
            generalSpecificVariant !== 'velio',
          'px-[16px] py-[12px]': generalSpecificVariant === 'velio',
        }
      )}
    >
      <div className="flex items-center gap-[12px]">
        {config.ui?.header?.banner && (
          <img className="max-h-[48px]" src={config.ui?.header?.banner} alt="banner" />
        )}
        {haveIcon && !config.ui?.header?.banner && (
          <div className="flex size-[40px] items-center justify-center gap-2 rounded-md bg-widget-accent-600 text-white">
            {icon ? (
              <img src={icon} alt="icon" />
            ) : (
              <svg
                width="20"
                height="21"
                viewBox="0 0 20 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 5.8C1 4.11984 1 3.27976 1.32698 2.63803C1.6146 2.07354 2.07354 1.6146 2.63803 1.32698C3.27976 1 4.11984 1 5.8 1H14.2C15.8802 1 16.7202 1 17.362 1.32698C17.9265 1.6146 18.3854 2.07354 18.673 2.63803C19 3.27976 19 4.11984 19 5.8V11.2C19 12.8802 19 13.7202 18.673 14.362C18.3854 14.9265 17.9265 15.3854 17.362 15.673C16.7202 16 15.8802 16 14.2 16H7.68375C7.0597 16 6.74767 16 6.44921 16.0613C6.18443 16.1156 5.9282 16.2055 5.68749 16.3285C5.41617 16.4671 5.17252 16.662 4.68521 17.0518L2.29976 18.9602C1.88367 19.2931 1.67563 19.4595 1.50054 19.4597C1.34827 19.4599 1.20422 19.3906 1.10923 19.2716C1 19.1348 1 18.8684 1 18.3355V5.8Z"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        )}
        <div className="flex flex-col">
          {haveTitle && title && (
            <h2 className="text-md font-semibold text-widget-text-primary">{title}</h2>
          )}
          {haveSupportiveText && supportiveText && (
            <p className="text-sm text-widget-text-secondary">{supportiveText}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {haveRestartButton && (
          <button
            onClick={resetSession}
            className={cn(buttonClassName, 'flex items-center gap-2')}
            aria-label="Restart chat"
          >
            {fullScreen && <span className="text-sm font-medium">Reset</span>}
            <RestartSvg className={cn('h-5 w-5', iconClassName)} />
          </button>
        )}
        {haveSettingsButton && (
          <button onClick={() => undefined} className={buttonClassName} aria-label="Open settings">
            <Settings className={cn('h-5 w-5', iconClassName)} />
          </button>
        )}
        {!isMobile && haveFullScreenMode && (
          <button onClick={toggleFullScreen} className={buttonClassName}>
            {fullScreen ? (
              <Minimize2 className={cn('h-5 w-5', iconClassName)} />
            ) : (
              <Maximize2 className={cn('h-5 w-5', iconClassName)} />
            )}
          </button>
        )}
        <button onClick={toggleOpen} className={cn(buttonClassName, 'border-0')}>
          <X className={cn('h-5 w-5', iconClassName)} />
        </button>
      </div>
    </div>
  );
};

export default ChatHeader;
